import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import dayjs from 'dayjs';
import Yup from '../../utils/yup';
import utc from 'dayjs/plugin/utc';

import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { cleanValues } from '../../utils/helper';
import { ButtonUI, DeleteDialogUI, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import serviceCoreService from '../../service/serviceCore.service';
import ServiceFields from './ServiceFields';
import ServiceTabs from './ServiceTabs';
import { MaterialPolicy } from '../../config/service';
import { useParams } from 'react-router-dom';

const ServicePage = ({ serviceState, dialog }) => {
  const {
    setRefetchTable,
    viewField,
    setViewField,
    selectedValues,
    setSelectedValues,
    operatorsDisabled,
    setOperatorsDisabled,
    setRowSelectionModel,
    autocompleteOptions,
    user,
    canEdit,
    autocompleteData,
    defaultValues,
    setDefaultValues,
    hasPrintTemplate,
  } = serviceState;
  const { id } = useParams();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);

  const [initValues, setInitValues] = useState({});

  const def = {
    workingNumber: '',
    serviceOperationTaskListId: null,
    projectId: null,
    itemCode: null,
    projectOrderNumber: '',
    serviceTaskTypeId: null,
    costCenterId: null,
    companyId: null,
    serviceStatusId: null,
    priorityId: null,
    userId: null,
    materialPolicy: MaterialPolicy.ANY,
    startDate: dayjs(),
    endDate: dayjs(),
    dueDate: dayjs(),
    description: '',
    reference: '',
    serviceTask: [],
    serviceTaskCheckList: [],
    serviceMaterial: [],
  };

  const defaultServiceTask = {
    serviceId: null,
    serviceOperationId: null,
    plannedTime: 1,
    qtyUnitId: null,
    serviceStatusId: defaultValues.serviceStatusId || null,
    workAreaId: null,
    visOrder: null,
    responsiblePersonId: null,
    note: '',
    progressPercent: 0,
    label: '',
    attachRequired: false,
    descriptionRequired: false,
  };

  const defaultServiceMaterial = {
    serviceId: null,
    serviceTaskId: null,
    itemCode: null,
    itemName: '',
    qty: 1,
    qtyUnitId: null,
    isRequired: false,
    note: '',
  };

  const defaultServiceTaskCheckList = {
    serviceId: null,
    serviceTaskId: null,
    name: '',
    note: '',
    attachRequired: false,
    descriptionRequired: false,
  };

  useEffect(() => {
    if (id) {
      getRowById();
    } else {
      setSelectedValues(defaultValues);
    }
  }, [defaultValues, id]);

  useEffect(() => {
    setDefaultValues(def);
  }, []);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  useEffect(() => {
    setSelectedValues(defaultValues);
  }, [defaultValues]);

  const validationSchema = Yup.object().shape({
    workingNumber: Yup.string().nullable(),
    serviceOperationTaskListId: Yup.number().nullable(),
    projectId: Yup.number().nullable(),
    itemCode: Yup.string().nullable(),
    projectOrderNumber: Yup.string().nullable(),
    serviceTaskTypeId: Yup.number().required(),
    costCenterId: Yup.number().nullable(),
    companyId: Yup.number().nullable(),
    serviceStatusId: Yup.string().required(),
    priorityId: Yup.number().required(),
    userId: Yup.number().nullable(),
    materialPolicy: Yup.string().required(),
    startDate: Yup.date().required(),
    endDate: Yup.date().required(),
    dueDate: Yup.date().required(),
    description: Yup.string().nullable(),
    reference: Yup.string().nullable(),
    serviceTask: Yup.array().of(
      Yup.object().shape({
        serviceOperationId: Yup.number().required(),
        serviceId: Yup.number().nullable(),
        plannedTime: Yup.number().min(0).required(),
        qtyUnitId: Yup.number().nullable(),
        serviceStatusId: Yup.string().required(),
        workAreaId: Yup.number().nullable(),
        visOrder: Yup.number().nullable(),
        note: Yup.string().nullable(),
        progressPercent: Yup.number().min(0).max(100).required(),
        label: Yup.string().nullable(),
        attachRequired: Yup.boolean().required(),
        descriptionRequired: Yup.boolean().required(),
        responsiblePersonId: Yup.number().nullable(),
      })
    ),
    serviceMaterial: Yup.array().of(
      Yup.object().shape({
        serviceId: Yup.number().nullable(),
        serviceTaskId: Yup.string().nullable(),
        itemCode: Yup.string().required(),
        itemName: Yup.string().nullable(),
        qty: Yup.number().min(0).required(),
        qtyUnitId: Yup.number().nullable(),
        isRequired: Yup.boolean().required(),
        note: Yup.string().nullable(),
      })
    ),
    serviceTaskCheckList: Yup.array().of(
      Yup.object().shape({
        serviceId: Yup.number().required(),
        serviceTaskId: Yup.string().nullable(),
        name: Yup.string().required(),
        note: Yup.string().nullable(),
        attachRequired: Yup.boolean().required(),
        descriptionRequired: Yup.boolean().required(),
      })
    ),
  });

  const getRowById = () => {
    serviceCoreService.getItemById(id).then((data) => {
      setSelectedValues(data);
      setRowSelectionModel(data.id);
    });
  };

  const valuesToForm = () => {
    const {
      id,
      workingNumber,
      serviceOperationTaskListId,
      projectId,
      serviceTaskTypeId,
      costCenterId,
      companyId,
      serviceStatusId,
      priorityId,
      userId,
      materialPolicy,
      startDate,
      endDate,
      dueDate,
      description,
      reference,
      serviceTask,
      serviceMaterial,
      serviceTaskCheckList,
      itemCode,
    } = selectedValues;

    setInitValues({
      id,
      workingNumber,
      serviceOperationTaskListId,
      projectId,
      projectOrderNumber: selectedValues.project?.orderNumber || '',
      serviceTaskTypeId,
      costCenterId,
      companyId,
      serviceStatusId,
      priorityId,
      userId,
      materialPolicy,
      itemCode,
      startDate: dayjs(startDate),
      endDate: dayjs(endDate),
      dueDate: dayjs(dueDate),
      description: description ?? '',
      reference: reference ?? '',
      serviceTask: serviceTask.map((st) => ({
        ...st,
      })),
      serviceMaterial: serviceMaterial.map((sm) => ({
        ...sm,
      })),
      serviceTaskCheckList: serviceTaskCheckList.map((stc) => ({
        ...stc,
      })),
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...cleanValues(values),
      serviceTask: values.serviceTask.map((st) => {
        return {
          ...cleanValues(st),
        };
      }),
      serviceMaterial: values.serviceMaterial.map((sm) => {
        return {
          ...cleanValues(sm),
        };
      }),
      serviceTaskCheckList: values.serviceTaskCheckList.map((stc) => {
        return {
          ...cleanValues(stc),
        };
      }),
      updatedBy: user.userId,
    };

    serviceCoreService.updateItem(updData, selectedValues.id).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);

      if (dialog) {
        dialog.setServiceChanged(true);
        dialog.handleClose();
      }
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...cleanValues(values),
      serviceTask: values.serviceTask.map((st) => {
        return {
          ...cleanValues(st),
        };
      }),
      serviceMaterial: values.serviceMaterial.map((sm) => {
        return {
          ...cleanValues(sm),
        };
      }),
      serviceTaskCheckList: values.serviceTaskCheckList.map((stc) => {
        return {
          ...cleanValues(stc),
        };
      }),
      updatedBy: user.userId,
    };

    serviceCoreService
      .createItem(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  dayjs.extend(utc);

  const handleRemoveElement = async () => {
    serviceCoreService.deleteItem(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultValues);
      setInitValues(defaultValues);
    });
  };

  const deleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleExport = () => {
    serviceCoreService.getExcelData(selectedValues.itemCode).then(() => {
      addNotification({
        content: 'A letöltés elindult!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const onAddClick = () => {
    setInitValues(defaultValues);
    setSelectedValues(defaultValues);
    setViewField(false);
    setAddField(false);
  };

  const onCopyClick = () => {
    setViewField(false);
    setAddField(false);
  };

  const printClick = () => {
    addNotification({
      content: 'Nyomtatás elindult!',
      type: NotificationType.INFO,
    });

    serviceCoreService.printDocument({
      docId: selectedValues.id,
    });
  };

  return (
    <>
      <div className="h-full overflow-auto custom-scrollbar">
        <div className="flex items-center flex-wrap justify-between">
          <div className="flex items-center flex-wrap">
            <FormHeaderUI
              addClick={!dialog ? onAddClick : null}
              addDisabled={!viewField || !canEdit}
              modClick={() => {
                setViewField(false);
                setEditButtonvisible(false);
              }}
              modDisabled={!viewField || operatorsDisabled || !canEdit}
              deleteClick={!dialog ? deleteClick : null}
              deleteDisabled={!viewField || operatorsDisabled || !canEdit}
              copyClick={!dialog ? onCopyClick : null}
              copyDisabled={!viewField || operatorsDisabled || !canEdit}
            />
            <ButtonUI
              text="Nyomtatás"
              size="sm"
              className="bg-labelColor py-3"
              onClick={() => printClick()}
              disabled={!viewField || operatorsDisabled || !hasPrintTemplate}
            />
          </div>
        </div>

        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ resetForm }) => (
            <Form>
              <ServiceFields
                viewField={viewField}
                selectedValues={selectedValues}
                autocompleteOptions={autocompleteOptions}
                autocompleteData={autocompleteData}
              />

              <ServiceTabs
                disabled={viewField}
                defaultServiceTask={defaultServiceTask}
                defaultServiceMaterial={defaultServiceMaterial}
                defaultServiceTaskCheckList={defaultServiceTaskCheckList}
                autocompleteOptions={autocompleteOptions}
                autocompleteData={autocompleteData}
                setRefetchTable={setRefetchTable}
                selectedValuesState={{ selectedValues, setSelectedValues }}
                user={user}
                addField={addField}
              />

              <FormButtonPanelUI
                onCancelClick={() => {
                  setViewField(true);
                  setEditButtonvisible(true);
                  setAddField(true);
                  resetForm();
                  valuesToForm();
                }}
                disabled={editButtonVisible && addField}
              />
            </Form>
          )}
        </Formik>
      </div>
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

export default ServicePage;
